import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import api from '../../config/api';
import { exportStyledExcel, r2, MONEY_FMT, TOTAL_FILL } from '../../utils/exportExcel';
import { useFeatures } from '../../hooks/useFeatures';
import { ProfitLossView } from '../accounting/AccountingPlus';

// Pagination bypass: Reports need ALL docs/expenses/accounts — not just page 1.
const getAll = (url, params) =>
  api.get(url, { params: { ...params, all: true } }).then(r =>
    Array.isArray(r.data) ? r.data : (r.data?.data || [])
  );

const TABS = ['P&L', 'Sales', 'Purchases', 'Balances', 'Receivables', 'Payables', 'Expenses', 'Shifts'];

const formatDateLocal = (y, m, d) =>
  `${y}-${String(m).padStart(2,'0')}-${String(d).padStart(2,'0')}`;

const todayLocal = () => {
  const now = new Date();
  return formatDateLocal(now.getFullYear(), now.getMonth()+1, now.getDate());
};

const PERIODS = [
  { label: 'Today', getValue: () => {
    const d = todayLocal();
    return { from: d, to: d };
  }},
  { label: 'This Week', getValue: () => {
    const now = new Date();
    const day = now.getDay();
    const diff = day === 0 ? -6 : 1 - day;
    const mon = new Date(now);
    mon.setDate(now.getDate() + diff);
    return {
      from: formatDateLocal(mon.getFullYear(), mon.getMonth()+1, mon.getDate()),
      to:   todayLocal(),
    };
  }},
  { label: 'This Month', getValue: () => {
    const now = new Date();
    return {
      from: formatDateLocal(now.getFullYear(), now.getMonth()+1, 1),
      to:   todayLocal(),
    };
  }},
  { label: 'This Year', getValue: () => {
    const now = new Date();
    return {
      from: formatDateLocal(now.getFullYear(), 1, 1),
      to:   todayLocal(),
    };
  }},
  { label: 'Custom', getValue: () => ({ from: '', to: '' }) },
];

const toUSD = (amount, currency, rate) => {
  if (!amount) return 0;
  if (currency === 'USD') return Number(amount);
  return Number(amount) / (rate || 89500);
};

const fmtDate = (d) => new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
const fmtAmt  = (n) => `$${(n || 0).toFixed(2)}`;
// show an amount in its OWN currency (raw — never converted)
const fmtCur = (n, currency) => currency === 'LBP'
  ? `${Math.round(Number(n) || 0).toLocaleString('en-US')} LBP`
  : `$${(Number(n) || 0).toFixed(2)}`;

// sum raw open balances per currency from a list of confirmed docs
const balanceByCurrency = (docs) => {
  const m = {};
  docs.forEach(d => {
    const bal = Number(d.totals?.balance) || 0;
    if (bal <= 0) return;
    const c = d.currency || 'USD';
    (m[c] ||= { currency: c, balance: 0 }).balance += bal;
  });
  return Object.values(m);
};

// sum a totals field (e.g. 'total') per currency, raw — for sales/purchases breakdowns
const sumByCurrency = (docs, field = 'total') => {
  const m = {};
  docs.forEach(d => {
    const a = Number(d.totals?.[field]) || 0;
    if (!a) return;
    const c = d.currency || 'USD';
    (m[c] ||= { currency: c, balance: 0 }).balance += a;
  });
  return Object.values(m);
};

// small per-currency card row (rendered only when the second-currency feature is on)
function CurrencyBreakdown({ title, items }) {
  if (!items || items.length === 0) return null;
  return (
    <div className="mb-5">
      <div className="text-xs font-semibold uppercase tracking-wider text-gray-400 mb-2">{title}</div>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        {items.map(c => (
          <div key={c.currency} className="bg-white rounded-xl border border-gray-200 p-3 shadow-sm flex items-center justify-between">
            <span className="font-bold text-gray-600">{c.currency}</span>
            <span className="font-bold text-gray-900">{fmtCur(c.balance, c.currency)}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function SummaryCard({ label, value, color = 'text-gray-800', sub }) {
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
      <p className="text-xs text-gray-500 mb-1">{label}</p>
      <p className={`text-2xl font-bold ${color}`}>{value}</p>
      {sub && <p className="text-xs text-gray-400 mt-1">{sub}</p>}
    </div>
  );
}

function ExportBtn({ onClick }) {
  return (
    <button onClick={onClick}
      className="flex items-center gap-2 px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-medium rounded-lg">
      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
      </svg>
      Export Excel
    </button>
  );
}

function PrintBtn({ onClick }) {
  return (
    <button onClick={onClick}
      className="flex items-center gap-2 px-3 py-1.5 bg-gray-700 hover:bg-gray-800 text-white text-xs font-medium rounded-lg">
      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
      </svg>
      Print
    </button>
  );
}

function Spinner() {
  return <div className="flex justify-center py-16"><div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" /></div>;
}

// ── shared print helpers ─────────────────────────────────
const PRINT_STYLE = `body{font-family:Arial,sans-serif;padding:30px;font-size:13px;}
h2{margin-bottom:4px;}p.period{color:#666;margin-top:0;}
table{width:100%;border-collapse:collapse;margin-top:14px;}
th,td{padding:7px 10px;border-bottom:1px solid #eee;text-align:left;}
th{background:#f3f4f6;font-size:11px;text-transform:uppercase;}
td.r,th.r{text-align:right;}.bold{font-weight:bold;}
tfoot td{font-weight:bold;background:#f9fafb;border-top:2px solid #ddd;}`;

const esc = (s) => String(s ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;');

const openPrint = (title, period, bodyHTML) => {
  const win = window.open('', '_blank');
  if (!win) { alert('Pop-up blocked — allow pop-ups to print.'); return; }
  win.document.write(`<html><head><title>${esc(title)}</title><style>${PRINT_STYLE}</style></head><body><h2>${esc(title)}</h2><p class="period">${esc(period)}</p>${bodyHTML}</body></html>`);
  win.document.close(); win.focus(); win.print();
};

const periodLabel = (from, to) => `Period: ${from ? fmtDate(from) : 'All time'} — ${to ? fmtDate(to) : 'Today'}`;
const fileTag = (from, to) => `${from || 'all'}_${to || 'today'}`;

// ── Sales Tab ────────────────────────────────────────────
function SalesTab({ dateFrom, dateTo }) {
  const { data: docs = [], isLoading } = useQuery({
    queryKey: ['rpt-sales', dateFrom, dateTo],
    queryFn: () => getAll('/api/documents', { status: 'confirmed', type: 200, ...(dateFrom && { date_from: dateFrom }), ...(dateTo && { date_to: dateTo }) }),
  });
  const features = useFeatures();
  const { data: accounts = [] } = useQuery({
    queryKey: ['accounts'],
    queryFn: () => getAll('/api/accounts', {}),
  });

  if (isLoading) return <Spinner />;

  const accountMap = {};
  accounts.forEach(a => { accountMap[String(a._id)] = a; });

  const totalRevenue = docs.reduce((s, d) => s + toUSD(d.totals?.total   || 0, d.currency, d.exchange_rate_snapshot), 0);
  const totalPaid    = docs.reduce((s, d) => s + toUSD(d.totals?.paid    || 0, d.currency, d.exchange_rate_snapshot), 0);
  const totalBalance = docs.reduce((s, d) => s + toUSD(d.totals?.balance || 0, d.currency, d.exchange_rate_snapshot), 0);
  const byCur = sumByCurrency(docs, 'total');

  const byAccount = {};
  docs.forEach(d => {
    const key  = d.account_id ? String(d.account_id) : 'walkin';
    const name = d.account_id ? (accountMap[key]?.name || 'Unknown') : 'Walk-in';
    if (!byAccount[key]) byAccount[key] = { name, total: 0, paid: 0, balance: 0, count: 0 };
    byAccount[key].total   += toUSD(d.totals?.total   || 0, d.currency, d.exchange_rate_snapshot);
    byAccount[key].paid    += toUSD(d.totals?.paid    || 0, d.currency, d.exchange_rate_snapshot);
    byAccount[key].balance += toUSD(d.totals?.balance || 0, d.currency, d.exchange_rate_snapshot);
    byAccount[key].count++;
  });

  const handleExport = () => exportStyledExcel({
    filename: `sales_${fileTag(dateFrom, dateTo)}`,
    sheetName: 'Sales',
    title: 'Sales Report',
    subtitle: `${periodLabel(dateFrom, dateTo)} · ${docs.length} documents`,
    columns: [
      { header: 'Number', key: 'number', width: 14 },
      { header: 'Date', key: 'date', width: 13 },
      { header: 'Customer', key: 'customer', width: 26 },
      { header: 'Currency', key: 'currency', width: 10 },
      { header: 'Amount (raw)', key: 'raw', width: 18 },
      { header: 'Total (USD eq.)', key: 'total', width: 15, numFmt: MONEY_FMT, align: 'right' },
      { header: 'Paid (USD eq.)', key: 'paid', width: 15, numFmt: MONEY_FMT, align: 'right' },
      { header: 'Balance (USD eq.)', key: 'balance', width: 17, numFmt: MONEY_FMT, align: 'right' },
    ],
    rows: [
      ...docs.map(d => ({
        number: d.number, date: fmtDate(d.date),
        customer: d.account_id ? (accountMap[String(d.account_id)]?.name || 'Unknown') : 'Walk-in',
        currency: d.currency,
        raw: fmtCur(d.totals?.total, d.currency),
        total: r2(toUSD(d.totals?.total, d.currency, d.exchange_rate_snapshot)),
        paid: r2(toUSD(d.totals?.paid, d.currency, d.exchange_rate_snapshot)),
        balance: r2(toUSD(d.totals?.balance, d.currency, d.exchange_rate_snapshot)),
      })),
      { number: 'TOTAL', customer: `${docs.length} docs`, total: r2(totalRevenue), paid: r2(totalPaid), balance: r2(totalBalance), __bold: true, __fill: TOTAL_FILL },
    ],
  });

  const handlePrint = () => {
    const rows = docs.map(d => `<tr><td>${esc(d.number)}</td><td>${fmtDate(d.date)}</td><td>${esc(d.account_id ? (accountMap[String(d.account_id)]?.name || 'Unknown') : 'Walk-in')}</td><td class="r">${esc(fmtCur(d.totals?.total, d.currency))}</td><td class="r">${fmtAmt(toUSD(d.totals?.total, d.currency, d.exchange_rate_snapshot))}</td><td class="r">${fmtAmt(toUSD(d.totals?.paid, d.currency, d.exchange_rate_snapshot))}</td><td class="r">${fmtAmt(toUSD(d.totals?.balance, d.currency, d.exchange_rate_snapshot))}</td></tr>`).join('');
    openPrint('Sales Report', periodLabel(dateFrom, dateTo),
      `<table><thead><tr><th>Number</th><th>Date</th><th>Customer</th><th class="r">Amount</th><th class="r">Total (USD)</th><th class="r">Paid (USD)</th><th class="r">Balance (USD)</th></tr></thead><tbody>${rows}</tbody><tfoot><tr><td colspan="4">Total (${docs.length} docs)</td><td class="r">${fmtAmt(totalRevenue)}</td><td class="r">${fmtAmt(totalPaid)}</td><td class="r">${fmtAmt(totalBalance)}</td></tr></tfoot></table>`);
  };

  return (
    <div>
      <div className="flex justify-end gap-2 mb-4"><PrintBtn onClick={handlePrint} /><ExportBtn onClick={handleExport} /></div>
      <div className="grid grid-cols-3 gap-4 mb-5">
        <SummaryCard label="Total Sales" value={fmtAmt(totalRevenue)} color="text-green-600" sub={`${docs.length} documents`} />
        <SummaryCard label="Collected"   value={fmtAmt(totalPaid)}    color="text-blue-600" />
        <SummaryCard label="Outstanding" value={fmtAmt(totalBalance)} color={totalBalance > 0 ? 'text-red-500' : 'text-green-600'} />
      </div>
      {features.second_currency && <CurrencyBreakdown title="Sales by currency" items={byCur} />}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          <div className="px-4 py-3 border-b border-gray-100 font-semibold text-sm text-gray-700">By Customer</div>
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-xs uppercase text-gray-500 border-b border-gray-100">
              <tr>
                <th className="text-left px-4 py-2">Customer</th>
                <th className="text-right px-4 py-2">Total</th>
                <th className="text-right px-4 py-2">Paid</th>
                <th className="text-right px-4 py-2">Balance</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {Object.values(byAccount).sort((a,b) => b.total - a.total).map(a => (
                <tr key={a.name} className="hover:bg-gray-50">
                  <td className="px-4 py-2.5 font-medium text-gray-700">{a.name} <span className="text-gray-400 font-normal text-xs">({a.count})</span></td>
                  <td className="px-4 py-2.5 text-right">{fmtAmt(a.total)}</td>
                  <td className="px-4 py-2.5 text-right text-green-600">{fmtAmt(a.paid)}</td>
                  <td className="px-4 py-2.5 text-right text-red-500">{fmtAmt(a.balance)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          <div className="px-4 py-3 border-b border-gray-100 font-semibold text-sm text-gray-700">Documents ({docs.length})</div>
          <div className="overflow-y-auto max-h-80">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-xs uppercase text-gray-500 border-b border-gray-100 sticky top-0">
                <tr>
                  <th className="text-left px-4 py-2">Number</th>
                  <th className="text-left px-4 py-2">Date</th>
                  <th className="text-left px-4 py-2">Customer</th>
                  <th className="text-right px-4 py-2">Total</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {docs.map(d => (
                  <tr key={d._id} className="hover:bg-gray-50">
                    <td className="px-4 py-2 font-mono text-blue-600 font-semibold text-xs">{d.number}</td>
                    <td className="px-4 py-2 text-gray-500 text-xs">{fmtDate(d.date)}</td>
                    <td className="px-4 py-2 text-gray-600 text-xs">{d.account_id ? (accountMap[String(d.account_id)]?.name || 'Unknown') : 'Walk-in'}</td>
                    <td className="px-4 py-2 text-right font-medium">{fmtCur(d.totals?.total, d.currency)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Purchases Tab ────────────────────────────────────────
function PurchasesTab({ dateFrom, dateTo }) {
  const { data: docs = [], isLoading } = useQuery({
    queryKey: ['rpt-purchases', dateFrom, dateTo],
    queryFn: () => getAll('/api/documents', { status: 'confirmed', type: 100, ...(dateFrom && { date_from: dateFrom }), ...(dateTo && { date_to: dateTo }) }),
  });
  const features = useFeatures();
  const { data: accounts = [] } = useQuery({
    queryKey: ['accounts'],
    queryFn: () => getAll('/api/accounts', {}),
  });

  if (isLoading) return <Spinner />;

  const accountMap = {};
  accounts.forEach(a => { accountMap[String(a._id)] = a; });

  const total   = docs.reduce((s, d) => s + toUSD(d.totals?.total   || 0, d.currency, d.exchange_rate_snapshot), 0);
  const paid    = docs.reduce((s, d) => s + toUSD(d.totals?.paid    || 0, d.currency, d.exchange_rate_snapshot), 0);
  const balance = docs.reduce((s, d) => s + toUSD(d.totals?.balance || 0, d.currency, d.exchange_rate_snapshot), 0);
  const byCur = sumByCurrency(docs, 'total');

  const bySupplier = {};
  docs.forEach(d => {
    const key  = d.account_id ? String(d.account_id) : 'unknown';
    const name = d.account_id ? (accountMap[key]?.name || 'Unknown') : 'Unknown';
    if (!bySupplier[key]) bySupplier[key] = { name, total: 0, paid: 0, balance: 0, count: 0 };
    bySupplier[key].total   += toUSD(d.totals?.total   || 0, d.currency, d.exchange_rate_snapshot);
    bySupplier[key].paid    += toUSD(d.totals?.paid    || 0, d.currency, d.exchange_rate_snapshot);
    bySupplier[key].balance += toUSD(d.totals?.balance || 0, d.currency, d.exchange_rate_snapshot);
    bySupplier[key].count++;
  });

  const handleExport = () => exportStyledExcel({
    filename: `purchases_${fileTag(dateFrom, dateTo)}`,
    sheetName: 'Purchases',
    title: 'Purchases Report',
    subtitle: `${periodLabel(dateFrom, dateTo)} · ${docs.length} documents`,
    columns: [
      { header: 'Number', key: 'number', width: 14 },
      { header: 'Date', key: 'date', width: 13 },
      { header: 'Supplier', key: 'supplier', width: 26 },
      { header: 'Currency', key: 'currency', width: 10 },
      { header: 'Amount (raw)', key: 'raw', width: 18 },
      { header: 'Total (USD eq.)', key: 'total', width: 15, numFmt: MONEY_FMT, align: 'right' },
      { header: 'Paid (USD eq.)', key: 'paid', width: 15, numFmt: MONEY_FMT, align: 'right' },
      { header: 'Balance (USD eq.)', key: 'balance', width: 17, numFmt: MONEY_FMT, align: 'right' },
    ],
    rows: [
      ...docs.map(d => ({
        number: d.number, date: fmtDate(d.date),
        supplier: d.account_id ? (accountMap[String(d.account_id)]?.name || 'Unknown') : 'Unknown',
        currency: d.currency,
        raw: fmtCur(d.totals?.total, d.currency),
        total: r2(toUSD(d.totals?.total, d.currency, d.exchange_rate_snapshot)),
        paid: r2(toUSD(d.totals?.paid, d.currency, d.exchange_rate_snapshot)),
        balance: r2(toUSD(d.totals?.balance, d.currency, d.exchange_rate_snapshot)),
      })),
      { number: 'TOTAL', supplier: `${docs.length} docs`, total: r2(total), paid: r2(paid), balance: r2(balance), __bold: true, __fill: TOTAL_FILL },
    ],
  });

  const handlePrint = () => {
    const rows = docs.map(d => `<tr><td>${esc(d.number)}</td><td>${fmtDate(d.date)}</td><td>${esc(d.account_id ? (accountMap[String(d.account_id)]?.name || 'Unknown') : 'Unknown')}</td><td class="r">${esc(fmtCur(d.totals?.total, d.currency))}</td><td class="r">${fmtAmt(toUSD(d.totals?.total, d.currency, d.exchange_rate_snapshot))}</td><td class="r">${fmtAmt(toUSD(d.totals?.paid, d.currency, d.exchange_rate_snapshot))}</td><td class="r">${fmtAmt(toUSD(d.totals?.balance, d.currency, d.exchange_rate_snapshot))}</td></tr>`).join('');
    openPrint('Purchases Report', periodLabel(dateFrom, dateTo),
      `<table><thead><tr><th>Number</th><th>Date</th><th>Supplier</th><th class="r">Amount</th><th class="r">Total (USD)</th><th class="r">Paid (USD)</th><th class="r">Balance (USD)</th></tr></thead><tbody>${rows}</tbody><tfoot><tr><td colspan="4">Total (${docs.length} docs)</td><td class="r">${fmtAmt(total)}</td><td class="r">${fmtAmt(paid)}</td><td class="r">${fmtAmt(balance)}</td></tr></tfoot></table>`);
  };

  return (
    <div>
      <div className="flex justify-end gap-2 mb-4"><PrintBtn onClick={handlePrint} /><ExportBtn onClick={handleExport} /></div>
      <div className="grid grid-cols-3 gap-4 mb-5">
        <SummaryCard label="Total Purchased" value={fmtAmt(total)}   color="text-red-500"  sub={`${docs.length} documents`} />
        <SummaryCard label="Paid"            value={fmtAmt(paid)}    color="text-blue-600" />
        <SummaryCard label="Still Owe"       value={fmtAmt(balance)} color={balance > 0 ? 'text-red-500' : 'text-green-600'} />
      </div>
      {features.second_currency && <CurrencyBreakdown title="Purchases by currency" items={byCur} />}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          <div className="px-4 py-3 border-b border-gray-100 font-semibold text-sm text-gray-700">By Supplier</div>
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-xs uppercase text-gray-500 border-b border-gray-100">
              <tr>
                <th className="text-left px-4 py-2">Supplier</th>
                <th className="text-right px-4 py-2">Total</th>
                <th className="text-right px-4 py-2">Paid</th>
                <th className="text-right px-4 py-2">Balance</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {Object.values(bySupplier).sort((a,b) => b.total - a.total).map(s => (
                <tr key={s.name} className="hover:bg-gray-50">
                  <td className="px-4 py-2.5 font-medium text-gray-700">{s.name} <span className="text-gray-400 font-normal text-xs">({s.count})</span></td>
                  <td className="px-4 py-2.5 text-right">{fmtAmt(s.total)}</td>
                  <td className="px-4 py-2.5 text-right text-green-600">{fmtAmt(s.paid)}</td>
                  <td className="px-4 py-2.5 text-right text-red-500">{fmtAmt(s.balance)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          <div className="px-4 py-3 border-b border-gray-100 font-semibold text-sm text-gray-700">Documents ({docs.length})</div>
          <div className="overflow-y-auto max-h-80">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-xs uppercase text-gray-500 border-b border-gray-100 sticky top-0">
                <tr>
                  <th className="text-left px-4 py-2">Number</th>
                  <th className="text-left px-4 py-2">Date</th>
                  <th className="text-left px-4 py-2">Supplier</th>
                  <th className="text-right px-4 py-2">Total</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {docs.map(d => (
                  <tr key={d._id} className="hover:bg-gray-50">
                    <td className="px-4 py-2 font-mono text-blue-600 font-semibold text-xs">{d.number}</td>
                    <td className="px-4 py-2 text-gray-500 text-xs">{fmtDate(d.date)}</td>
                    <td className="px-4 py-2 text-gray-600 text-xs">{d.account_id ? (accountMap[String(d.account_id)]?.name || 'Unknown') : 'Unknown'}</td>
                    <td className="px-4 py-2 text-right font-medium">{fmtCur(d.totals?.total, d.currency)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Balances Tab — unified debit/credit per account ──────
function BalancesTab() {
  const { data: accounts = [], isLoading: la } = useQuery({
    queryKey: ['accounts'],
    queryFn: () => getAll('/api/accounts', {}),
  });
  const { data: salesDocs = [], isLoading: l2 } = useQuery({
    queryKey: ['rpt-bal-sales'],
    queryFn: () => getAll('/api/documents', { status: 'confirmed', type: 200 }),
  });
  const { data: purchDocs = [], isLoading: l3 } = useQuery({
    queryKey: ['rpt-bal-purch'],
    queryFn: () => getAll('/api/documents', { status: 'confirmed', type: 100 }),
  });
  const { data: retDocs = [], isLoading: l4 } = useQuery({
    queryKey: ['rpt-bal-ret'],
    queryFn: () => getAll('/api/documents', { status: 'confirmed', type: 220 }),
  });
  const { data: prDocs = [], isLoading: l5 } = useQuery({
    queryKey: ['rpt-bal-pr'],
    queryFn: () => getAll('/api/documents', { status: 'confirmed', type: 120 }),
  });

  const [filter, setFilter] = useState('all'); // 'all' | 'debit' | 'credit'
  const [search, setSearch] = useState('');

  if (la || l2 || l3 || l4 || l5) return <Spinner />;

  // build per-account balance map
  const map = {};
  const ensure = (aid, acc) => {
    if (!map[aid]) map[aid] = { account: acc, receivable: 0, payable: 0 };
  };

  // opening balances
  accounts.forEach(a => {
    const aid = String(a._id);
    ensure(aid, a);
    const ob = Number(a.opening_balance) || 0;
    if (ob > 0) {
      if (a.opening_balance_type === 'credit') map[aid].payable   += ob; // you owe them
      else                                      map[aid].receivable += ob; // they owe you
    }
  });

  // sales invoices (200): customers owe you → receivable
  salesDocs.forEach(d => {
    if (!d.account_id) return;
    const aid = String(d.account_id);
    ensure(aid, accounts.find(a => String(a._id) === aid));
    map[aid].receivable += toUSD(d.totals?.balance || 0, d.currency, d.exchange_rate_snapshot);
  });

  // sales returns (220): reduce receivable
  retDocs.forEach(d => {
    if (!d.account_id) return;
    const aid = String(d.account_id);
    ensure(aid, accounts.find(a => String(a._id) === aid));
    map[aid].receivable -= toUSD(d.totals?.total || 0, d.currency, d.exchange_rate_snapshot);
  });

  // purchase invoices (100): you owe suppliers → payable
  purchDocs.forEach(d => {
    if (!d.account_id) return;
    const aid = String(d.account_id);
    ensure(aid, accounts.find(a => String(a._id) === aid));
    map[aid].payable += toUSD(d.totals?.balance || 0, d.currency, d.exchange_rate_snapshot);
  });

  // purchase returns (120): reduce payable
  prDocs.forEach(d => {
    if (!d.account_id) return;
    const aid = String(d.account_id);
    ensure(aid, accounts.find(a => String(a._id) === aid));
    map[aid].payable -= toUSD(d.totals?.total || 0, d.currency, d.exchange_rate_snapshot);
  });

  let rows = Object.values(map).map(r => ({
    ...r,
    receivable: Math.max(0, r.receivable),
    payable:    Math.max(0, r.payable),
    net:        r.receivable - r.payable,
  })).filter(r => r.receivable > 0.005 || r.payable > 0.005);

  // filter
  if (filter === 'debit')  rows = rows.filter(r => r.receivable > r.payable);
  if (filter === 'credit') rows = rows.filter(r => r.payable > r.receivable);
  if (search.trim()) {
    const q = search.trim().toLowerCase();
    rows = rows.filter(r => r.account?.name?.toLowerCase().includes(q) || r.account?.phone?.includes(q));
  }
  rows.sort((a, b) => Math.abs(b.net) - Math.abs(a.net));

  const totalReceivable = rows.reduce((s, r) => s + r.receivable, 0);
  const totalPayable    = rows.reduce((s, r) => s + r.payable, 0);
  const netTotal        = totalReceivable - totalPayable;

  const handleExport = () => exportStyledExcel({
    filename: 'account_balances',
    sheetName: 'Balances',
    title: 'Account Balances Report',
    subtitle: `As of ${fmtDate(new Date())} · all time`,
    columns: [
      { header: 'Account',     key: 'name',        width: 26 },
      { header: 'Phone',       key: 'phone',       width: 16 },
      { header: 'Receivable',  key: 'receivable',  width: 16, numFmt: MONEY_FMT, align: 'right' },
      { header: 'Payable',     key: 'payable',     width: 16, numFmt: MONEY_FMT, align: 'right' },
      { header: 'Net',         key: 'net',         width: 16, numFmt: MONEY_FMT, align: 'right' },
      { header: 'Direction',   key: 'dir',         width: 14 },
    ],
    rows: [
      ...rows.map(r => ({
        name: r.account?.name || 'Unknown',
        phone: r.account?.phone || '',
        receivable: r2(r.receivable),
        payable:    r2(r.payable),
        net:        r2(r.net),
        dir: r.net > 0.005 ? 'They owe you' : r.net < -0.005 ? 'You owe them' : 'Settled',
      })),
      { name: 'TOTAL', phone: `${rows.length} accounts`, receivable: r2(totalReceivable), payable: r2(totalPayable), net: r2(netTotal), dir: '', __bold: true, __fill: TOTAL_FILL },
    ],
  });

  const handlePrint = () => {
    const trs = rows.map(r => {
      const dir = r.net > 0.005 ? '← They owe you' : r.net < -0.005 ? '→ You owe them' : '✓ Settled';
      return `<tr><td>${esc(r.account?.name || 'Unknown')}</td><td>${esc(r.account?.phone || '—')}</td><td class="r green">${fmtAmt(r.receivable)}</td><td class="r red">${fmtAmt(r.payable)}</td><td class="r ${r.net > 0 ? 'green' : r.net < 0 ? 'red' : ''}">${fmtAmt(Math.abs(r.net))}</td><td>${dir}</td></tr>`;
    }).join('');
    openPrint('Account Balances', `As of ${fmtDate(new Date())} — all time`,
      `<style>.green{color:#16a34a}.red{color:#dc2626}</style><table><thead><tr><th>Account</th><th>Phone</th><th class="r">Receivable</th><th class="r">Payable</th><th class="r">Net</th><th>Direction</th></tr></thead><tbody>${trs}</tbody><tfoot><tr><td colspan="2">TOTAL (${rows.length} accounts)</td><td class="r green">${fmtAmt(totalReceivable)}</td><td class="r red">${fmtAmt(totalPayable)}</td><td class="r ${netTotal >= 0 ? 'green' : 'red'}">${fmtAmt(Math.abs(netTotal))}</td><td>${netTotal >= 0 ? '← Net receivable' : '→ Net payable'}</td></tr></tfoot></table>`);
  };

  return (
    <div>
      <div className="flex justify-end gap-2 mb-4"><PrintBtn onClick={handlePrint} /><ExportBtn onClick={handleExport} /></div>

      {/* Summary cards */}
      <div className="grid grid-cols-3 gap-4 mb-5">
        <SummaryCard label="Total Receivable" value={fmtAmt(totalReceivable)} color="text-green-600" sub="They owe you" />
        <SummaryCard label="Total Payable"    value={fmtAmt(totalPayable)}    color="text-red-500"   sub="You owe them" />
        <SummaryCard
          label={netTotal >= 0 ? 'Net — You Are Owed' : 'Net — You Owe'}
          value={fmtAmt(Math.abs(netTotal))}
          color={netTotal >= 0 ? 'text-green-600' : 'text-red-500'}
          sub={`${rows.length} accounts with balance`}
        />
      </div>

      {/* Filter + Search */}
      <div className="flex flex-wrap gap-2 mb-4 items-center">
        {[['all','All'],['debit','They owe you 🟢'],['credit','You owe them 🔴']].map(([v, l]) => (
          <button key={v} onClick={() => setFilter(v)}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${filter === v ? 'bg-blue-600 text-white' : 'bg-gray-100 hover:bg-gray-200 text-gray-700'}`}>
            {l}
          </button>
        ))}
        <input value={search} onChange={e => setSearch(e.target.value)}
          placeholder="Search account…"
          className="ml-auto border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-blue-400" style={{ width: 200 }} />
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-xs uppercase text-gray-500 border-b border-gray-200">
            <tr>
              <th className="text-left px-4 py-3">Account</th>
              <th className="text-left px-4 py-3">Phone</th>
              <th className="text-right px-4 py-3 text-green-600">Receivable</th>
              <th className="text-right px-4 py-3 text-red-500">Payable</th>
              <th className="text-right px-4 py-3">Net</th>
              <th className="text-left px-4 py-3">Status</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {rows.length === 0 ? (
              <tr><td colSpan={6} className="px-4 py-10 text-center text-gray-400">No account balances found.</td></tr>
            ) : rows.map(r => {
              const net = r.net;
              const isDebit  = net > 0.005;
              const isCredit = net < -0.005;
              return (
                <tr key={r.account?._id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 font-semibold text-gray-800">{r.account?.name || 'Unknown'}</td>
                  <td className="px-4 py-3 text-gray-500">{r.account?.phone || '—'}</td>
                  <td className="px-4 py-3 text-right text-green-700 font-medium">{r.receivable > 0.005 ? fmtAmt(r.receivable) : '—'}</td>
                  <td className="px-4 py-3 text-right text-red-500 font-medium">{r.payable > 0.005 ? fmtAmt(r.payable) : '—'}</td>
                  <td className={`px-4 py-3 text-right font-bold ${isDebit ? 'text-green-600' : isCredit ? 'text-red-500' : 'text-gray-400'}`}>
                    {isDebit ? '+' : isCredit ? '−' : ''}{fmtAmt(Math.abs(net))}
                  </td>
                  <td className="px-4 py-3">
                    {isDebit  && <span className="inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full bg-green-50 text-green-700">← They owe you</span>}
                    {isCredit && <span className="inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full bg-red-50 text-red-600">→ You owe them</span>}
                    {!isDebit && !isCredit && <span className="text-xs text-gray-400">✓ Settled</span>}
                  </td>
                </tr>
              );
            })}
          </tbody>
          {rows.length > 0 && (
            <tfoot className="bg-gray-50 border-t-2 border-gray-200 font-bold text-sm">
              <tr>
                <td colSpan={2} className="px-4 py-3 text-gray-700">Total ({rows.length} accounts)</td>
                <td className="px-4 py-3 text-right text-green-600">{fmtAmt(totalReceivable)}</td>
                <td className="px-4 py-3 text-right text-red-500">{fmtAmt(totalPayable)}</td>
                <td className={`px-4 py-3 text-right font-bold ${netTotal >= 0 ? 'text-green-600' : 'text-red-500'}`}>
                  {netTotal >= 0 ? '+' : '−'}{fmtAmt(Math.abs(netTotal))}
                </td>
                <td className="px-4 py-3 text-xs text-gray-500">{netTotal >= 0 ? '← Net receivable' : '→ Net payable'}</td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </div>
  );
}

// ── Receivables Tab ──────────────────────────────────────
function ReceivablesTab() {
  const { data: accounts = [], isLoading: la } = useQuery({
    queryKey: ['accounts'],
    queryFn: () => getAll('/api/accounts', {}),
  });
  const { data: docs = [], isLoading: ld } = useQuery({
    queryKey: ['rpt-receivables-docs'],
    queryFn: () => getAll('/api/documents', { status: 'confirmed', type: 200 }),
  });
  const features = useFeatures();

  if (la || ld) return <Spinner />;

  const accountMap = {};
  accounts.forEach(a => { accountMap[String(a._id)] = a; });

  const byAccount = {};
  docs.forEach(d => {
    const balanceUSD = toUSD(d.totals?.balance || 0, d.currency, d.exchange_rate_snapshot);
    if (balanceUSD <= 0 || !d.account_id) return;
    const aid = String(d.account_id);
    if (!byAccount[aid]) byAccount[aid] = { account: accountMap[aid], total: 0, paid: 0, balance: 0, docs: [] };
    byAccount[aid].total   += toUSD(d.totals?.total || 0, d.currency, d.exchange_rate_snapshot);
    byAccount[aid].paid    += toUSD(d.totals?.paid  || 0, d.currency, d.exchange_rate_snapshot);
    byAccount[aid].balance += balanceUSD;
    byAccount[aid].docs.push(d);
  });

  const rows = Object.values(byAccount).filter(r => r.balance > 0).sort((a,b) => b.balance - a.balance);
  const totalReceivable = rows.reduce((s, r) => s + r.balance, 0);
  const byCur = balanceByCurrency(docs.filter(d => d.account_id));

  const handleExport = () => exportStyledExcel({
    filename: 'receivables_report',
    sheetName: 'Receivables',
    title: 'Receivables Report (USD)',
    subtitle: `As of ${fmtDate(new Date())} · money customers owe you`,
    columns: [
      { header: 'Customer', key: 'customer', width: 26 },
      { header: 'Phone', key: 'phone', width: 16 },
      { header: 'Billed (USD)', key: 'billed', width: 14, numFmt: MONEY_FMT, align: 'right' },
      { header: 'Paid (USD)', key: 'paid', width: 14, numFmt: MONEY_FMT, align: 'right' },
      { header: 'Balance Due (USD)', key: 'balance', width: 17, numFmt: MONEY_FMT, align: 'right' },
      { header: 'Docs', key: 'docs', width: 8, align: 'right' },
    ],
    rows: [
      ...rows.map(r => ({
        customer: r.account?.name || 'Unknown', phone: r.account?.phone || '',
        billed: r2(r.total), paid: r2(r.paid), balance: r2(r.balance), docs: r.docs.length,
      })),
      { customer: 'TOTAL', phone: `${rows.length} customers`, balance: r2(totalReceivable), __bold: true, __fill: TOTAL_FILL },
    ],
  });

  const handlePrint = () => {
    const trs = rows.map(r => `<tr><td>${esc(r.account?.name || 'Unknown')}</td><td>${esc(r.account?.phone || '—')}</td><td class="r">${fmtAmt(r.total)}</td><td class="r">${fmtAmt(r.paid)}</td><td class="r">${fmtAmt(r.balance)}</td><td class="r">${r.docs.length}</td></tr>`).join('');
    openPrint('Receivables Report (USD)', `As of ${fmtDate(new Date())} — money customers owe you`,
      `<table><thead><tr><th>Customer</th><th>Phone</th><th class="r">Billed</th><th class="r">Paid</th><th class="r">Balance Due</th><th class="r">Docs</th></tr></thead><tbody>${trs}</tbody><tfoot><tr><td colspan="4">Total Receivable (${rows.length} customers)</td><td class="r">${fmtAmt(totalReceivable)}</td><td></td></tr></tfoot></table>`);
  };

  return (
    <div>
      <div className="flex justify-end gap-2 mb-4"><PrintBtn onClick={handlePrint} /><ExportBtn onClick={handleExport} /></div>
      <div className="grid grid-cols-2 gap-4 mb-5">
        <SummaryCard label="Total Receivable" value={fmtAmt(totalReceivable)} color="text-red-500" sub="Money customers owe you" />
        <SummaryCard label="Customers"        value={rows.length}             color="text-gray-800" sub="With outstanding balance" />
      </div>
      {features.second_currency && <CurrencyBreakdown title="Receivable by currency" items={byCur} />}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-xs uppercase text-gray-500 border-b border-gray-200">
            <tr>
              <th className="text-left px-4 py-3">Customer</th>
              <th className="text-left px-4 py-3">Phone</th>
              <th className="text-right px-4 py-3">Billed</th>
              <th className="text-right px-4 py-3">Paid</th>
              <th className="text-right px-4 py-3">Balance Due</th>
              <th className="text-right px-4 py-3">Docs</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {rows.length === 0 ? (
              <tr><td colSpan={6} className="px-4 py-10 text-center text-gray-400">No outstanding receivables.</td></tr>
            ) : rows.map(r => (
              <tr key={r.account?._id} className="hover:bg-gray-50">
                <td className="px-4 py-2.5 font-medium text-gray-800">{r.account?.name || 'Unknown'}</td>
                <td className="px-4 py-2.5 text-gray-500">{r.account?.phone || '—'}</td>
                <td className="px-4 py-2.5 text-right">{fmtAmt(r.total)}</td>
                <td className="px-4 py-2.5 text-right text-green-600">{fmtAmt(r.paid)}</td>
                <td className="px-4 py-2.5 text-right font-bold text-red-500">{fmtAmt(r.balance)}</td>
                <td className="px-4 py-2.5 text-right text-gray-400">{r.docs.length}</td>
              </tr>
            ))}
          </tbody>
          {rows.length > 0 && (
            <tfoot className="bg-gray-50 border-t-2 border-gray-200 font-bold text-sm">
              <tr>
                <td colSpan={4} className="px-4 py-3 text-gray-700">Total Receivable</td>
                <td className="px-4 py-3 text-right text-red-500">{fmtAmt(totalReceivable)}</td>
                <td></td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </div>
  );
}

// ── Payables Tab ─────────────────────────────────────────
function PayablesTab() {
  const { data: accounts = [], isLoading: la } = useQuery({
    queryKey: ['accounts'],
    queryFn: () => getAll('/api/accounts', {}),
  });
  const { data: docs = [], isLoading: ld } = useQuery({
    queryKey: ['rpt-payables-docs'],
    queryFn: () => getAll('/api/documents', { status: 'confirmed', type: 100 }),
  });
  const features = useFeatures();

  if (la || ld) return <Spinner />;

  const accountMap = {};
  accounts.forEach(a => { accountMap[String(a._id)] = a; });

  const byAccount = {};
  docs.forEach(d => {
    const balanceUSD = toUSD(d.totals?.balance || 0, d.currency, d.exchange_rate_snapshot);
    if (balanceUSD <= 0 || !d.account_id) return;
    const aid = String(d.account_id);
    if (!byAccount[aid]) byAccount[aid] = { account: accountMap[aid], total: 0, paid: 0, balance: 0, docs: [] };
    byAccount[aid].total   += toUSD(d.totals?.total || 0, d.currency, d.exchange_rate_snapshot);
    byAccount[aid].paid    += toUSD(d.totals?.paid  || 0, d.currency, d.exchange_rate_snapshot);
    byAccount[aid].balance += balanceUSD;
    byAccount[aid].docs.push(d);
  });

  const rows = Object.values(byAccount).filter(r => r.balance > 0).sort((a,b) => b.balance - a.balance);
  const totalPayable = rows.reduce((s, r) => s + r.balance, 0);
  const byCur = balanceByCurrency(docs.filter(d => d.account_id));

  const handleExport = () => exportStyledExcel({
    filename: 'payables_report',
    sheetName: 'Payables',
    title: 'Payables Report (USD)',
    subtitle: `As of ${fmtDate(new Date())} · money you owe suppliers`,
    columns: [
      { header: 'Supplier', key: 'supplier', width: 26 },
      { header: 'Phone', key: 'phone', width: 16 },
      { header: 'Purchased (USD)', key: 'purchased', width: 16, numFmt: MONEY_FMT, align: 'right' },
      { header: 'Paid (USD)', key: 'paid', width: 14, numFmt: MONEY_FMT, align: 'right' },
      { header: 'Balance Due (USD)', key: 'balance', width: 17, numFmt: MONEY_FMT, align: 'right' },
      { header: 'Docs', key: 'docs', width: 8, align: 'right' },
    ],
    rows: [
      ...rows.map(r => ({
        supplier: r.account?.name || 'Unknown', phone: r.account?.phone || '',
        purchased: r2(r.total), paid: r2(r.paid), balance: r2(r.balance), docs: r.docs.length,
      })),
      { supplier: 'TOTAL', phone: `${rows.length} suppliers`, balance: r2(totalPayable), __bold: true, __fill: TOTAL_FILL },
    ],
  });

  const handlePrint = () => {
    const trs = rows.map(r => `<tr><td>${esc(r.account?.name || 'Unknown')}</td><td>${esc(r.account?.phone || '—')}</td><td class="r">${fmtAmt(r.total)}</td><td class="r">${fmtAmt(r.paid)}</td><td class="r">${fmtAmt(r.balance)}</td><td class="r">${r.docs.length}</td></tr>`).join('');
    openPrint('Payables Report (USD)', `As of ${fmtDate(new Date())} — money you owe suppliers`,
      `<table><thead><tr><th>Supplier</th><th>Phone</th><th class="r">Purchased</th><th class="r">Paid</th><th class="r">Balance Due</th><th class="r">Docs</th></tr></thead><tbody>${trs}</tbody><tfoot><tr><td colspan="4">Total Payable (${rows.length} suppliers)</td><td class="r">${fmtAmt(totalPayable)}</td><td></td></tr></tfoot></table>`);
  };

  return (
    <div>
      <div className="flex justify-end gap-2 mb-4"><PrintBtn onClick={handlePrint} /><ExportBtn onClick={handleExport} /></div>
      <div className="grid grid-cols-2 gap-4 mb-5">
        <SummaryCard label="Total Payable" value={fmtAmt(totalPayable)} color="text-red-500" sub="Money you owe suppliers" />
        <SummaryCard label="Suppliers"     value={rows.length}          color="text-gray-800" sub="With outstanding balance" />
      </div>
      {features.second_currency && <CurrencyBreakdown title="Payable by currency" items={byCur} />}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-xs uppercase text-gray-500 border-b border-gray-200">
            <tr>
              <th className="text-left px-4 py-3">Supplier</th>
              <th className="text-left px-4 py-3">Phone</th>
              <th className="text-right px-4 py-3">Purchased</th>
              <th className="text-right px-4 py-3">Paid</th>
              <th className="text-right px-4 py-3">Balance Due</th>
              <th className="text-right px-4 py-3">Docs</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {rows.length === 0 ? (
              <tr><td colSpan={6} className="px-4 py-10 text-center text-gray-400">No outstanding payables.</td></tr>
            ) : rows.map(r => (
              <tr key={r.account?._id} className="hover:bg-gray-50">
                <td className="px-4 py-2.5 font-medium text-gray-800">{r.account?.name || 'Unknown'}</td>
                <td className="px-4 py-2.5 text-gray-500">{r.account?.phone || '—'}</td>
                <td className="px-4 py-2.5 text-right">{fmtAmt(r.total)}</td>
                <td className="px-4 py-2.5 text-right text-green-600">{fmtAmt(r.paid)}</td>
                <td className="px-4 py-2.5 text-right font-bold text-red-500">{fmtAmt(r.balance)}</td>
                <td className="px-4 py-2.5 text-right text-gray-400">{r.docs.length}</td>
              </tr>
            ))}
          </tbody>
          {rows.length > 0 && (
            <tfoot className="bg-gray-50 border-t-2 border-gray-200 font-bold text-sm">
              <tr>
                <td colSpan={4} className="px-4 py-3 text-gray-700">Total Payable</td>
                <td className="px-4 py-3 text-right text-red-500">{fmtAmt(totalPayable)}</td>
                <td></td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </div>
  );
}

// ── Expenses Tab ─────────────────────────────────────────
function ExpensesTab({ dateFrom, dateTo }) {
  const { data: expenses = [], isLoading } = useQuery({
    queryKey: ['rpt-expenses-tab', dateFrom, dateTo],
    queryFn: () => getAll('/api/expenses', { ...(dateFrom && { date_from: dateFrom }), ...(dateTo && { date_to: dateTo }) }),
  });

  if (isLoading) return <Spinner />;

  const totalPaid    = expenses.filter(e => e.status === 'paid').reduce((s, e) => s + toUSD(e.amount, e.currency, e.exchange_rate_snapshot || 89500), 0);
  const totalPending = expenses.filter(e => e.status === 'pending').reduce((s, e) => s + toUSD(e.amount, e.currency, e.exchange_rate_snapshot || 89500), 0);

  const byCategory = expenses.reduce((acc, e) => {
    const amtUSD = toUSD(e.amount, e.currency, e.exchange_rate_snapshot || 89500);
    if (!acc[e.category]) acc[e.category] = { category: e.category, paid: 0, pending: 0, total: 0, count: 0 };
    acc[e.category].total += amtUSD;
    if (e.status === 'paid') acc[e.category].paid += amtUSD;
    else acc[e.category].pending += amtUSD;
    acc[e.category].count++;
    return acc;
  }, {});

  const handleExport = () => exportStyledExcel({
    filename: `expenses_${fileTag(dateFrom, dateTo)}`,
    sheetName: 'Expenses',
    title: 'Expenses Report (USD)',
    subtitle: `${periodLabel(dateFrom, dateTo)} · ${expenses.length} expenses`,
    columns: [
      { header: 'Date', key: 'date', width: 13 },
      { header: 'Category', key: 'category', width: 18 },
      { header: 'Description', key: 'description', width: 28 },
      { header: 'Method', key: 'method', width: 14 },
      { header: 'Status', key: 'status', width: 10 },
      { header: 'Amount (USD)', key: 'usd', width: 14, numFmt: MONEY_FMT, align: 'right' },
      { header: 'Original', key: 'original', width: 14, numFmt: MONEY_FMT, align: 'right' },
      { header: 'Currency', key: 'currency', width: 10 },
    ],
    rows: [
      ...expenses.map(e => ({
        date: fmtDate(e.date), category: e.category, description: e.description || '',
        method: e.payment_method_name || '', status: e.status,
        usd: r2(toUSD(e.amount, e.currency, e.exchange_rate_snapshot || 89500)),
        original: r2(e.amount), currency: e.currency,
      })),
      { date: 'TOTAL', category: `${expenses.length} expenses`, usd: r2(totalPaid + totalPending), __bold: true, __fill: TOTAL_FILL },
    ],
  });

  const handlePrint = () => {
    const trs = expenses.map(e => `<tr><td>${fmtDate(e.date)}</td><td>${esc(e.category)}${e.description ? ` — ${esc(e.description)}` : ''}</td><td>${esc(e.status)}</td><td class="r">${fmtAmt(toUSD(e.amount, e.currency, e.exchange_rate_snapshot || 89500))}</td></tr>`).join('');
    openPrint('Expenses Report (USD)', periodLabel(dateFrom, dateTo),
      `<table><thead><tr><th>Date</th><th>Category</th><th>Status</th><th class="r">Amount</th></tr></thead><tbody>${trs}</tbody><tfoot><tr><td colspan="3">Total (${expenses.length} expenses — paid ${fmtAmt(totalPaid)}, pending ${fmtAmt(totalPending)})</td><td class="r">${fmtAmt(totalPaid + totalPending)}</td></tr></tfoot></table>`);
  };

  return (
    <div>
      <div className="flex justify-end gap-2 mb-4"><PrintBtn onClick={handlePrint} /><ExportBtn onClick={handleExport} /></div>
      <div className="grid grid-cols-3 gap-4 mb-5">
        <SummaryCard label="Total Expenses" value={fmtAmt(totalPaid + totalPending)} color="text-gray-800" sub={`${expenses.length} expenses`} />
        <SummaryCard label="Paid"           value={fmtAmt(totalPaid)}               color="text-red-500" />
        <SummaryCard label="Pending"        value={fmtAmt(totalPending)}            color="text-yellow-600" />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          <div className="px-4 py-3 border-b border-gray-100 font-semibold text-sm text-gray-700">By Category</div>
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-xs uppercase text-gray-500 border-b border-gray-100">
              <tr>
                <th className="text-left px-4 py-2">Category</th>
                <th className="text-right px-4 py-2 text-red-500">Paid</th>
                <th className="text-right px-4 py-2 text-yellow-600">Pending</th>
                <th className="text-right px-4 py-2">Total</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {Object.values(byCategory).sort((a,b) => b.total - a.total).map(c => (
                <tr key={c.category} className="hover:bg-gray-50">
                  <td className="px-4 py-2.5 font-medium text-gray-700">{c.category} <span className="text-gray-400 font-normal text-xs">({c.count})</span></td>
                  <td className="px-4 py-2.5 text-right text-red-500">{fmtAmt(c.paid)}</td>
                  <td className="px-4 py-2.5 text-right text-yellow-600">{fmtAmt(c.pending)}</td>
                  <td className="px-4 py-2.5 text-right font-bold text-gray-800">{fmtAmt(c.total)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          <div className="px-4 py-3 border-b border-gray-100 font-semibold text-sm text-gray-700">All Expenses ({expenses.length})</div>
          <div className="overflow-y-auto max-h-80">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-xs uppercase text-gray-500 border-b border-gray-100 sticky top-0">
                <tr>
                  <th className="text-left px-4 py-2">Date</th>
                  <th className="text-left px-4 py-2">Category</th>
                  <th className="text-right px-4 py-2">Amount</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {expenses.map(e => (
                  <tr key={e._id} className="hover:bg-gray-50">
                    <td className="px-4 py-2 text-gray-500 text-xs">{fmtDate(e.date)}</td>
                    <td className="px-4 py-2 text-gray-700">{e.category}{e.description ? ` — ${e.description}` : ''}</td>
                    <td className="px-4 py-2 text-right font-medium text-red-500">{fmtAmt(toUSD(e.amount, e.currency, e.exchange_rate_snapshot || 89500))}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Shifts Tab (cashier shifts: expected vs counted cash, over/short) ────
function ShiftsTab({ dateFrom, dateTo }) {
  const { data: shifts = [], isLoading } = useQuery({
    queryKey: ['rpt-shifts-tab', dateFrom, dateTo],
    queryFn: () => api.get('/api/shifts', {
      params: { ...(dateFrom && { date_from: dateFrom }), ...(dateTo && { date_to: dateTo }) }
    }).then(r => r.data),
  });

  if (isLoading) return <Spinner />;

  const fmtLBP = (n) => `${Math.round(n || 0).toLocaleString()}`;
  const fmtTime = (d) => d ? new Date(d).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '—';
  const closed = shifts.filter(s => s.status === 'closed');
  const open   = shifts.filter(s => s.status === 'open');
  // over/short rolled into USD for the summary card (LBP converted at 89500 snapshot-less)
  const diffUSD = (s) => (s.difference?.usd || 0) + (s.difference?.lbp || 0) / 89500;
  const totalDiff = closed.reduce((sum, s) => sum + diffUSD(s), 0);
  const withDiff  = closed.filter(s => Math.abs(diffUSD(s)) > 0.01).length;

  // per-cashier over/short
  const byCashier = closed.reduce((acc, s) => {
    const k = s.user_name || s.user_id;
    if (!acc[k]) acc[k] = { name: k, shifts: 0, diff: 0 };
    acc[k].shifts++; acc[k].diff += diffUSD(s);
    return acc;
  }, {});

  const handleExport = () => exportStyledExcel({
    filename: `shifts_${fileTag(dateFrom, dateTo)}`,
    sheetName: 'Shifts',
    title: 'Cashier Shifts Report',
    subtitle: `${periodLabel(dateFrom, dateTo)} · ${shifts.length} shifts`,
    columns: [
      { header: 'Cashier', key: 'cashier', width: 18 },
      { header: 'Opened', key: 'opened', width: 18 },
      { header: 'Closed', key: 'closedAt', width: 18 },
      { header: 'Opening $', key: 'openUsd', width: 12, numFmt: MONEY_FMT, align: 'right' },
      { header: 'Opening LBP', key: 'openLbp', width: 14, align: 'right' },
      { header: 'Expected $', key: 'expUsd', width: 12, numFmt: MONEY_FMT, align: 'right' },
      { header: 'Expected LBP', key: 'expLbp', width: 14, align: 'right' },
      { header: 'Counted $', key: 'cntUsd', width: 12, numFmt: MONEY_FMT, align: 'right' },
      { header: 'Counted LBP', key: 'cntLbp', width: 14, align: 'right' },
      { header: 'Diff $', key: 'difUsd', width: 11, numFmt: MONEY_FMT, align: 'right' },
      { header: 'Diff LBP', key: 'difLbp', width: 13, align: 'right' },
      { header: 'Handed to', key: 'handed', width: 16 },
    ],
    rows: [
      ...shifts.map(s => ({
        cashier: s.user_name || '', opened: new Date(s.opened_at).toLocaleString('en-GB'),
        closedAt: s.closed_at ? new Date(s.closed_at).toLocaleString('en-GB') : 'OPEN',
        openUsd: r2(s.opening_cash?.usd), openLbp: Math.round(s.opening_cash?.lbp || 0),
        expUsd: s.expected_cash ? r2(s.expected_cash.usd) : '', expLbp: s.expected_cash ? Math.round(s.expected_cash.lbp) : '',
        cntUsd: s.counted_cash ? r2(s.counted_cash.usd) : '', cntLbp: s.counted_cash ? Math.round(s.counted_cash.lbp) : '',
        difUsd: s.difference ? r2(s.difference.usd) : '', difLbp: s.difference ? Math.round(s.difference.lbp) : '',
        handed: s.handover_to_name || '',
      })),
      { cashier: 'TOTAL over/short', difUsd: r2(totalDiff), __bold: true, __fill: TOTAL_FILL },
    ],
  });

  const handlePrint = () => {
    const trs = closed.map(s => `<tr><td>${esc(s.user_name || '')}</td><td>${fmtDate(s.opened_at)} ${fmtTime(s.opened_at)} → ${fmtTime(s.closed_at)}</td><td class="r">$${(s.expected_cash?.usd || 0).toFixed(2)} · ${fmtLBP(s.expected_cash?.lbp)}</td><td class="r">$${(s.counted_cash?.usd || 0).toFixed(2)} · ${fmtLBP(s.counted_cash?.lbp)}</td><td class="r">$${(s.difference?.usd || 0).toFixed(2)} · ${fmtLBP(s.difference?.lbp)}</td></tr>`).join('');
    openPrint('Cashier Shifts Report', periodLabel(dateFrom, dateTo),
      `<table><thead><tr><th>Cashier</th><th>Shift</th><th class="r">Expected</th><th class="r">Counted</th><th class="r">Difference</th></tr></thead><tbody>${trs}</tbody><tfoot><tr><td colspan="4">Total over/short (${closed.length} closed shifts)</td><td class="r">${fmtAmt(totalDiff)}</td></tr></tfoot></table>`);
  };

  return (
    <div>
      <div className="flex justify-end gap-2 mb-4"><PrintBtn onClick={handlePrint} /><ExportBtn onClick={handleExport} /></div>
      <div className="grid grid-cols-3 gap-4 mb-5">
        <SummaryCard label="Shifts" value={String(shifts.length)} sub={`${open.length} open now`} />
        <SummaryCard label="Total Over/Short" value={fmtAmt(totalDiff)} color={Math.abs(totalDiff) < 0.01 ? 'text-green-600' : totalDiff < 0 ? 'text-red-500' : 'text-yellow-600'} sub="USD equivalent" />
        <SummaryCard label="Shifts w/ Discrepancy" value={String(withDiff)} color={withDiff ? 'text-red-500' : 'text-green-600'} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          <div className="px-4 py-3 border-b border-gray-100 font-semibold text-sm text-gray-700">Over/Short by Cashier</div>
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-xs uppercase text-gray-500 border-b border-gray-100">
              <tr><th className="text-left px-4 py-2">Cashier</th><th className="text-right px-4 py-2">Shifts</th><th className="text-right px-4 py-2">Diff</th></tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {Object.values(byCashier).sort((a, b) => a.diff - b.diff).map(c => (
                <tr key={c.name} className="hover:bg-gray-50">
                  <td className="px-4 py-2.5 font-medium text-gray-700">{c.name}</td>
                  <td className="px-4 py-2.5 text-right text-gray-500">{c.shifts}</td>
                  <td className={`px-4 py-2.5 text-right font-medium ${Math.abs(c.diff) < 0.01 ? 'text-green-600' : c.diff < 0 ? 'text-red-500' : 'text-yellow-600'}`}>{fmtAmt(c.diff)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="md:col-span-2 bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          <div className="px-4 py-3 border-b border-gray-100 font-semibold text-sm text-gray-700">All Shifts</div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-xs uppercase text-gray-500 border-b border-gray-100">
                <tr>
                  <th className="text-left px-4 py-2">Cashier</th>
                  <th className="text-left px-4 py-2">Opened → Closed</th>
                  <th className="text-right px-4 py-2">Expected</th>
                  <th className="text-right px-4 py-2">Counted</th>
                  <th className="text-right px-4 py-2">Diff</th>
                  <th className="text-left px-4 py-2">Handed to</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {shifts.map(s => {
                  const d = diffUSD(s);
                  return (
                    <tr key={s._id} className="hover:bg-gray-50">
                      <td className="px-4 py-2.5 font-medium text-gray-700">{s.user_name}</td>
                      <td className="px-4 py-2.5 text-gray-500 text-xs">
                        {fmtDate(s.opened_at)} {fmtTime(s.opened_at)} → {s.status === 'open'
                          ? <span className="text-green-600 font-semibold">OPEN</span>
                          : fmtTime(s.closed_at)}
                      </td>
                      <td className="px-4 py-2.5 text-right text-gray-600 text-xs">
                        {s.expected_cash ? <>${(s.expected_cash.usd || 0).toFixed(2)}<br />{fmtLBP(s.expected_cash.lbp)} L</> : '—'}
                      </td>
                      <td className="px-4 py-2.5 text-right text-gray-600 text-xs">
                        {s.counted_cash ? <>${(s.counted_cash.usd || 0).toFixed(2)}<br />{fmtLBP(s.counted_cash.lbp)} L</> : '—'}
                      </td>
                      <td className={`px-4 py-2.5 text-right font-semibold ${s.status === 'open' ? 'text-gray-400' : Math.abs(d) < 0.01 ? 'text-green-600' : d < 0 ? 'text-red-500' : 'text-yellow-600'}`}>
                        {s.status === 'open' ? '—' : fmtAmt(d)}
                      </td>
                      <td className="px-4 py-2.5 text-gray-500 text-xs">{s.handover_to_name || '—'}</td>
                    </tr>
                  );
                })}
                {!shifts.length && <tr><td colSpan={6} className="px-4 py-8 text-center text-gray-400">No shifts in this period</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Main Reports Page ────────────────────────────────────
export default function Reports() {
  const features = useFeatures();
  const visibleTabs = features.shifts === false ? TABS.filter(tb => tb !== 'Shifts') : TABS;
  const [activeTab, setActiveTab] = useState('P&L');
  const [activePeriod, setActivePeriod] = useState('This Month');
  const [dateFrom, setDateFrom] = useState(() => {
    const now = new Date();
    return formatDateLocal(now.getFullYear(), now.getMonth()+1, 1);
  });
  const [dateTo, setDateTo] = useState(() => todayLocal());

  const handlePeriod = (p) => {
    setActivePeriod(p.label);
    if (p.label !== 'Custom') {
      const { from, to } = p.getValue();
      setDateFrom(from);
      setDateTo(to);
    }
  };

  return (
    <div className="p-6 text-gray-800">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Reports</h1>
        <p className="text-gray-500 text-sm mt-0.5">All amounts displayed in USD</p>
      </div>

      <div className="bg-white border border-gray-200 rounded-xl p-4 mb-5 flex flex-wrap gap-3 items-center">
        <div className="flex gap-1 flex-wrap">
          {PERIODS.map(p => (
            <button key={p.label} onClick={() => handlePeriod(p)}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${activePeriod === p.label ? 'bg-blue-600 text-white' : 'bg-gray-100 hover:bg-gray-200 text-gray-700'}`}>
              {p.label}
            </button>
          ))}
        </div>
        {activePeriod === 'Custom' && (
          <div className="flex items-center gap-2 text-sm">
            <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none" />
            <span className="text-gray-400">to</span>
            <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none" />
          </div>
        )}
        {activePeriod !== 'Custom' && (
          <span className="text-xs text-gray-400 ml-2">{fmtDate(dateFrom)} — {fmtDate(dateTo)}</span>
        )}
      </div>

      <div className="flex gap-1 mb-5 border-b border-gray-200 overflow-x-auto">
        {visibleTabs.map(tab => (
          <button key={tab} onClick={() => setActiveTab(tab)}
            className={`px-4 py-2.5 text-sm font-medium whitespace-nowrap transition-colors ${activeTab === tab ? 'border-b-2 border-blue-600 text-blue-600' : 'text-gray-500 hover:text-gray-700'}`}>
            {tab}
          </button>
        ))}
      </div>

      {/* P&L comes from the accounting engine on the server — same numbers as Accounting */}
      {activeTab === 'P&L'         && <ProfitLossView params={{ date_from: dateFrom, date_to: dateTo }} endpoint="/api/reports/pnl" />}
      {activeTab === 'Sales'       && <SalesTab     dateFrom={dateFrom} dateTo={dateTo} />}
      {activeTab === 'Purchases'   && <PurchasesTab dateFrom={dateFrom} dateTo={dateTo} />}
      {activeTab === 'Balances'    && <BalancesTab />}
      {activeTab === 'Receivables' && <ReceivablesTab />}
      {activeTab === 'Payables'    && <PayablesTab />}
      {activeTab === 'Expenses'    && <ExpensesTab  dateFrom={dateFrom} dateTo={dateTo} />}
      {activeTab === 'Shifts'      && <ShiftsTab    dateFrom={dateFrom} dateTo={dateTo} />}
    </div>
  );
}
